import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import {
  crearClienteExternoAltaRapida,
  formatAltaRapidaDniDisplay,
  formatMedicamentoOptionLabel,
  mapAltaRapidaPedidoResponse,
  mapClienteExternoToPaciente,
  normalizeAltaRapidaDni,
  resolvePacienteFromHistorial
} from '../utils/altaRapidaPedido';
import '../styles/alta-rapida-pedido.css';

function MedicamentoSearchStatus({ loading, query, count }) {
  const q = String(query || '').trim();
  if (loading) {
    return (
      <div className="crm-alta-rapida-med-status crm-alta-rapida-med-status--loading" role="status" aria-live="polite">
        <span className="crm-alta-rapida-med-status__spinner" aria-hidden />
        <div className="crm-alta-rapida-med-status__copy">
          <span className="crm-alta-rapida-med-status__title">
            {q ? 'Buscando en vademécum…' : 'Cargando catálogo de medicamentos…'}
          </span>
          <span className="crm-alta-rapida-med-status__meta">
            {q ? `Filtrando por “${q}”` : 'Esto puede tardar unos segundos'}
          </span>
        </div>
      </div>
    );
  }

  if (q) {
    return (
      <div className="crm-alta-rapida-med-status crm-alta-rapida-med-status--ready" role="status">
        <span className="crm-alta-rapida-med-status__icon" aria-hidden>✓</span>
        <div className="crm-alta-rapida-med-status__copy">
          <span className="crm-alta-rapida-med-status__title">
            <strong>{count}</strong> {count === 1 ? 'resultado' : 'resultados'} para tu búsqueda
          </span>
          <span className="crm-alta-rapida-med-status__meta">Elegí uno del listado desplegable</span>
        </div>
      </div>
    );
  }

  if (count > 0) {
    return (
      <div className="crm-alta-rapida-med-status crm-alta-rapida-med-status--catalog" role="status">
        <span className="crm-alta-rapida-med-status__icon" aria-hidden>💊</span>
        <div className="crm-alta-rapida-med-status__copy">
          <span className="crm-alta-rapida-med-status__title">
            Catálogo listo · <strong>{count}</strong> medicamentos
          </span>
          <span className="crm-alta-rapida-med-status__meta">Usá el buscador para filtrar más rápido</span>
        </div>
      </div>
    );
  }

  return null;
}

function MedicamentoPickerSkeleton({ rows = 5 }) {
  return (
    <div className="crm-alta-rapida-picker__skeleton" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div key={`med-sk-${i}`} className="crm-alta-rapida-picker__skeleton-row">
          <span className="crm-alta-rapida-picker__skeleton-line crm-alta-rapida-picker__skeleton-line--lg" />
          <span className="crm-alta-rapida-picker__skeleton-line crm-alta-rapida-picker__skeleton-line--sm" />
        </div>
      ))}
    </div>
  );
}

function PacienteDetectadoCard({ dni, paciente, loading }) {
  const dniFmt = formatAltaRapidaDniDisplay(dni);

  if (!normalizeAltaRapidaDni(dni)) {
    return (
      <div className="crm-alta-rapida-paciente-card crm-alta-rapida-paciente-card--idle">
        <div className="crm-alta-rapida-paciente-card__icon" aria-hidden>👤</div>
        <div className="crm-alta-rapida-paciente-card__main">
          <div className="crm-alta-rapida-paciente-card__title">Ingresá el DNI del paciente</div>
          <div className="crm-alta-rapida-paciente-card__meta">Buscamos en Plex/Geclisa y en tu historial de pedidos.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="crm-alta-rapida-paciente-card crm-alta-rapida-paciente-card--loading">
        <div className="crm-alta-rapida-paciente-card__icon" aria-hidden>⏳</div>
        <div className="crm-alta-rapida-paciente-card__main">
          <div className="crm-alta-rapida-paciente-card__title">Buscando paciente…</div>
          <div className="crm-alta-rapida-paciente-card__meta">DNI {dniFmt}</div>
        </div>
      </div>
    );
  }

  if (paciente?.paciente) {
    const isHistorial = paciente.source === 'historial';
    const isExterno = paciente.source === 'externo';
    const cardClass = isHistorial
      ? 'crm-alta-rapida-paciente-card--historial'
      : 'crm-alta-rapida-paciente-card--found';
    const badge = isHistorial ? 'Historial' : (isExterno ? 'Externo' : 'Plex/Geclisa');

    return (
      <div className={`crm-alta-rapida-paciente-card ${cardClass}`}>
        <div className="crm-alta-rapida-paciente-card__icon" aria-hidden>✓</div>
        <div className="crm-alta-rapida-paciente-card__main">
          <div className="crm-alta-rapida-paciente-card__title">{paciente.paciente}</div>
          <div className="crm-alta-rapida-paciente-card__meta">
            DNI {dniFmt}
            {paciente.obra_social ? ` · ${paciente.obra_social}` : ''}
            {paciente.prestador ? ` · ${paciente.prestador}` : ''}
          </div>
        </div>
        <span className="crm-alta-rapida-paciente-card__badge">{badge}</span>
      </div>
    );
  }

  return (
    <div className="crm-alta-rapida-paciente-card crm-alta-rapida-paciente-card--unknown">
      <div className="crm-alta-rapida-paciente-card__icon" aria-hidden>!</div>
      <div className="crm-alta-rapida-paciente-card__main">
        <div className="crm-alta-rapida-paciente-card__title">Paciente no encontrado</div>
        <div className="crm-alta-rapida-paciente-card__meta">
          DNI {dniFmt} — no está en Plex/Geclisa. En el siguiente paso vas a completar el alta.
        </div>
      </div>
      <span className="crm-alta-rapida-paciente-card__badge">Alta nueva</span>
    </div>
  );
}

function WizardSteps({ steps, currentStep }) {
  return (
    <div className="crm-alta-rapida-steps crm-alta-rapida-steps--dynamic" aria-label="Pasos del alta rápida">
      {steps.map((s, index) => {
        const stepNum = index + 1;
        const isDone = currentStep > stepNum;
        const isActive = currentStep === stepNum;
        return (
          <React.Fragment key={s.id}>
            {index > 0 ? (
              <div className={`crm-alta-rapida-steps__line${isDone ? ' is-done' : ''}`} aria-hidden />
            ) : null}
            <div
              className={`crm-alta-rapida-steps__item${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
            >
              <span className="crm-alta-rapida-steps__num">{isDone ? '✓' : stepNum}</span>
              <span className="crm-alta-rapida-steps__label">{s.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function AltaPacienteStep({
  dni,
  disabled,
  onSuccess,
  onError,
  onRefetchDni,
  onValidChange,
  onSubmittingChange
}) {
  const dniFmt = formatAltaRapidaDniDisplay(dni);
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [obraSocial, setObraSocial] = useState('');
  const [nroAfiliado, setNroAfiliado] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setNombreCompleto('');
    setFechaNacimiento('');
    setTelefono('');
    setEmail('');
    setObraSocial('');
    setNroAfiliado('');
    setSubmitting(false);
  }, [dni]);

  const validateFields = () => {
    const nombre = String(nombreCompleto || '').trim();
    const fecha = String(fechaNacimiento || '').trim();
    const tel = String(telefono || '').trim();
    const mail = String(email || '').trim();
    const os = String(obraSocial || '').trim();
    const afiliado = String(nroAfiliado || '').trim();

    if (nombre.length < 3) {
      return 'Ingresá nombre y apellido del paciente';
    }

    if (!fecha) {
      return 'Ingresá la fecha de nacimiento';
    }

    if (!tel || tel.length < 8) {
      return 'Ingresá un teléfono válido';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!mail || !emailRegex.test(mail)) {
      return 'Ingresá un email válido';
    }

    if (!os) {
      return 'Ingresá la obra social';
    }

    if (!afiliado) {
      return 'Ingresá el número de afiliado';
    }

    return null;
  };

  const canSubmit = !validateFields() && !submitting && !disabled;

  useEffect(() => {
    onValidChange?.(canSubmit);
  }, [canSubmit, onValidChange]);

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateFields();

    if (validationError) {
      onError?.(validationError);
      return;
    }

    const nombre = String(nombreCompleto || '').trim();

    setSubmitting(true);
    try {
      const row = await crearClienteExternoAltaRapida({
        nombre_completo: nombre,
        dni,
        fecha_nacimiento: fechaNacimiento || null,
        telefono: telefono || null,
        email: email || null,
        obra_social: obraSocial || null,
        nro_afiliado: nroAfiliado || null
      }, API_BASE_URL);
      const paciente = mapClienteExternoToPaciente(row);
      if (!paciente?.paciente) throw new Error('No se pudo registrar el paciente');
      onSuccess?.(paciente);
    } catch (err) {
      if (err?.code === 'DUPLICATE_DNI') {
        try {
          const refetched = await onRefetchDni?.();
          if (refetched?.paciente) {
            onSuccess?.(refetched);
            return;
          }
        } catch {
          /* fall through */
        }
        onError?.('Ese DNI ya está registrado. Volvé al paso anterior o contactá soporte.');
      } else {
        console.error('Error registrando paciente externo:', err);
        onError?.(err?.message || 'No se pudo registrar el paciente');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="crm-alta-paciente-form"
      className="crm-alta-rapida-alta-step"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="crm-alta-rapida-alta-step__identity">
        <div className="crm-alta-rapida-alta-step__avatar" aria-hidden>
          <span>{String(nombreCompleto || '?').trim().charAt(0).toUpperCase() || '?'}</span>
        </div>
        <div className="crm-alta-rapida-alta-step__identity-copy">
          <span className="crm-alta-rapida-alta-step__kicker">Nuevo registro</span>
          <h3 className="crm-alta-rapida-alta-step__title">Alta de paciente</h3>
          <p className="crm-alta-rapida-alta-step__subtitle">
            Ficha en <strong>clientes externos</strong> · disponible para pedidos y bots
          </p>
        </div>
        <div className="crm-alta-rapida-alta-step__dni-pill">
          <span className="crm-alta-rapida-alta-step__dni-label">DNI</span>
          <strong>{dniFmt}</strong>
        </div>
      </div>

      <div className="crm-alta-rapida-alta-step__sections">
        <section className="crm-alta-rapida-alta-section">
          <header className="crm-alta-rapida-alta-section__head">
            <span className="crm-alta-rapida-alta-section__icon" aria-hidden>👤</span>
            <div>
              <h4 className="crm-alta-rapida-alta-section__title">Identidad</h4>
              <p className="crm-alta-rapida-alta-section__desc">Datos principales del paciente</p>
            </div>
          </header>
          <div className="crm-alta-rapida-alta-section__body">
            <div className="crm-alta-rapida-field crm-alta-rapida-field--full">
              <label className="crm-alta-rapida-field__label" htmlFor="crm-alta-pac-nombre">
                Nombre y apellido <span className="crm-alta-rapida-field__req">*</span>
              </label>
              <input
                id="crm-alta-pac-nombre"
                className="crm-alta-rapida-field__input"
                value={nombreCompleto}
                onChange={(ev) => setNombreCompleto(ev.target.value)}
                placeholder="Juan López"
                autoComplete="name"
                disabled={disabled || submitting}
                required
              />
            </div>
            <div className="crm-alta-rapida-field" style={{ marginTop: '0.5rem' }}>
              <label className="crm-alta-rapida-field__label" htmlFor="crm-alta-pac-fnac">
                Fecha de nacimiento <span className="crm-alta-rapida-field__req">*</span>
              </label>
              <input
                id="crm-alta-pac-fnac"
                type="date"
                className="crm-alta-rapida-field__input crm-alta-rapida-field__input--date"
                value={fechaNacimiento}
                onChange={(ev) => setFechaNacimiento(ev.target.value)}
                disabled={disabled || submitting}
              />
            </div>
          </div>
        </section>

        <section className="crm-alta-rapida-alta-section">
          <header className="crm-alta-rapida-alta-section__head">
            <span className="crm-alta-rapida-alta-section__icon" aria-hidden>📱</span>
            <div>
              <h4 className="crm-alta-rapida-alta-section__title">Contacto</h4>
              <p className="crm-alta-rapida-alta-section__desc">Para recordatorios y seguimiento</p>
            </div>
          </header>
          <div className="crm-alta-rapida-alta-section__body crm-alta-rapida-alta-section__body--grid">
            <div className="crm-alta-rapida-field">
              <label className="crm-alta-rapida-field__label" htmlFor="crm-alta-pac-tel">
                Teléfono <span className="crm-alta-rapida-field__req">*</span>
              </label>
              <input
                id="crm-alta-pac-tel"
                type="tel"
                className="crm-alta-rapida-field__input"
                value={telefono}
                onChange={(ev) => setTelefono(ev.target.value.replace(/[^\d+\s()-]/g, ''))}
                placeholder="261 555 1234"
                inputMode="tel"
                disabled={disabled || submitting}
              />
            </div>
            <div className="crm-alta-rapida-field">
              <label className="crm-alta-rapida-field__label" htmlFor="crm-alta-pac-email">
                Email <span className="crm-alta-rapida-field__req">*</span>
              </label>
              <input
                id="crm-alta-pac-email"
                type="email"
                className="crm-alta-rapida-field__input"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="correo@ejemplo.com"
                autoComplete="email"
                disabled={disabled || submitting}
              />
            </div>
          </div>
        </section>

        <section className="crm-alta-rapida-alta-section crm-alta-rapida-alta-section--coverage">
          <header className="crm-alta-rapida-alta-section__head">
            <span className="crm-alta-rapida-alta-section__icon" aria-hidden>🛡️</span>
            <div>
              <h4 className="crm-alta-rapida-alta-section__title">Cobertura</h4>
              <p className="crm-alta-rapida-alta-section__desc">Obra social y afiliación</p>
            </div>
          </header>
          <div className="crm-alta-rapida-alta-section__body">
            <div className="crm-alta-rapida-alta-section__body--grid">
              <div className="crm-alta-rapida-field">
                <label className="crm-alta-rapida-field__label" htmlFor="crm-alta-pac-os">
                  Obra social <span className="crm-alta-rapida-field__req">*</span>
                </label>
                <input
                  id="crm-alta-pac-os"
                  className="crm-alta-rapida-field__input"
                  value={obraSocial}
                  onChange={(ev) => setObraSocial(ev.target.value)}
                  placeholder="OSEP, OSDE, PAMI…"
                  disabled={disabled || submitting}
                />
              </div>
              <div className="crm-alta-rapida-field">
                <label className="crm-alta-rapida-field__label" htmlFor="crm-alta-pac-afil">
                  Nº afiliado <span className="crm-alta-rapida-field__req">*</span></label>
                <input
                  id="crm-alta-pac-afil"
                  className="crm-alta-rapida-field__input"
                  value={nroAfiliado}
                  onChange={(ev) => setNroAfiliado(ev.target.value)}
                  placeholder="Opcional"
                  disabled={disabled || submitting}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </form>
  );
}

function PamiToggle({ value, onChange, disabled }) {
  return (
    <div className={`crm-alta-rapida-pami${value ? ' is-on' : ''}`}>
      <div className="crm-alta-rapida-pami__copy">
        <div className="crm-alta-rapida-pami__title">Cobertura PAMI</div>
        <p className="crm-alta-rapida-pami__desc">
          Activá si el paciente gestiona el tratamiento a través de PAMI.
        </p>
      </div>
      <div className="crm-alta-rapida-pami__switch-wrap">
        <span className={`crm-alta-rapida-pami__state${value ? ' is-on' : ''}`} aria-hidden>
          {value ? 'Sí' : 'No'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-label="Paciente PAMI"
          className={`crm-alta-rapida-pami__switch${value ? ' is-on' : ''}`}
          onClick={() => onChange(!value)}
          disabled={disabled}
        >
          <span className="crm-alta-rapida-pami__switch-knob" />
        </button>
      </div>
    </div>
  );
}

export default function AltaRapidaPedidoModal({
  open,
  onClose,
  historialPacientes = [],
  onPedidoCreated,
  onError,
  destino = 'aprobacion_medica',
  eyebrow = 'Alta manual',
  title = 'Alta rápida de pedido',
  subtitle = 'Seleccioná o ingresá DNI para crear un pedido pendiente de aprobación.',
  confirmQueueLabel = 'Entra a la cola del médico'
}) {
  const [dni, setDni] = useState('');
  const [medicamentoId, setMedicamentoId] = useState('');
  const [medicamentos, setMedicamentos] = useState([]);
  const [medicamentosLoading, setMedicamentosLoading] = useState(false);
  const [medicamentoSearch, setMedicamentoSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [medicamentoChosen, setMedicamentoChosen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pacienteApi, setPacienteApi] = useState(null);
  const [pacienteLoading, setPacienteLoading] = useState(false);
  const [esPami, setEsPami] = useState(false);
  const [step, setStep] = useState(1);
  const [altaStepIncluded, setAltaStepIncluded] = useState(false);
  const [altaFormValid, setAltaFormValid] = useState(false);
  const [altaSubmitting, setAltaSubmitting] = useState(false);
  const pickerRef = useRef(null);

  const dniNormalizado = normalizeAltaRapidaDni(dni);
  const medicamentoIdNum = Number(medicamentoId);

  const pacienteHistorial = useMemo(
    () => resolvePacienteFromHistorial(historialPacientes, dni),
    [historialPacientes, dni]
  );

  const pacienteResuelto = useMemo(() => {
    if (pacienteApi?.paciente) return pacienteApi;
    if (pacienteHistorial?.paciente && !pacienteLoading) return pacienteHistorial;
    return pacienteApi || pacienteHistorial;
  }, [pacienteApi, pacienteHistorial, pacienteLoading]);

  const pacienteListo = Boolean(pacienteResuelto?.paciente);
  const necesitaAltaPaciente = dniNormalizado.length >= 6
    && !pacienteLoading
    && !pacienteListo;
  const isAltaStep = altaStepIncluded && step === 2;
  const isMedStep = altaStepIncluded ? step === 3 : step === 2;
  const isDniStep = step === 1;
  const canLeaveDniStep = dniNormalizado.length >= 6 && !pacienteLoading;
  const canSubmit = pacienteListo
    && Number.isFinite(medicamentoIdNum)
    && medicamentoIdNum > 0;

  const wizardSteps = useMemo(() => {
    const list = [{ id: 'dni', label: 'Paciente' }];
    if (altaStepIncluded) list.push({ id: 'alta', label: 'Alta' });
    list.push({ id: 'med', label: 'Medicamento' });
    return list;
  }, [altaStepIncluded]);

  const refetchPacientePorDni = async () => {
    if (dniNormalizado.length < 6) return null;
    const res = await fetch(
      `${API_BASE_URL}/api/asociacion-recetas/clientes-por-dni/${encodeURIComponent(dniNormalizado)}`,
      { credentials: 'include' }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json?.found && json?.paciente) {
      const paciente = {
        dni: json.dni || dniNormalizado,
        paciente: json.paciente,
        obra_social: json.obra_social || '',
        source: json.source || 'geclisa',
        prestador: json.prestador_geclisa || null
      };
      setPacienteApi(paciente);
      return paciente;
    }
    return null;
  };

  const handlePacienteExternoRegistrado = (paciente) => {
    setPacienteApi(paciente);
    if (altaStepIncluded && step === 2) {
      setStep(3);
    }
  };

  const goBack = () => {
    if (isMedStep) {
      setStep(altaStepIncluded ? 2 : 1);
      return;
    }
    if (isAltaStep) {
      setStep(1);
    }
  };

  const goNextFromDni = () => {
    if (!dniNormalizado || dniNormalizado.length < 6) {
      onError?.('Ingresá un DNI válido (mínimo 6 dígitos)');
      return;
    }
    if (pacienteLoading) return;
    if (pacienteListo) {
      setAltaStepIncluded(false);
      setStep(2);
      return;
    }
    setAltaStepIncluded(true);
    setStep(2);
  };

  const resetForm = () => {
    setDni('');
    setMedicamentoId('');
    setMedicamentos([]);
    setMedicamentoSearch('');
    setPickerOpen(false);
    setMedicamentoChosen(null);
    setPacienteApi(null);
    setPacienteLoading(false);
    setEsPami(false);
    setStep(1);
    setAltaStepIncluded(false);
    setAltaFormValid(false);
    setAltaSubmitting(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const dniPrevRef = useRef('');
  useEffect(() => {
    if (dniPrevRef.current && dniPrevRef.current !== dniNormalizado) {
      setAltaStepIncluded(false);
      setStep(1);
      setAltaFormValid(false);
    }
    dniPrevRef.current = dniNormalizado;
  }, [dniNormalizado]);

  useEffect(() => {
    if (!open || dniNormalizado.length < 6) {
      setPacienteApi(null);
      setPacienteLoading(false);
      setAltaStepIncluded(false);
      if (step !== 1) setStep(1);
      return undefined;
    }

    const ac = new AbortController();
    const timer = setTimeout(async () => {
      setPacienteLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/asociacion-recetas/clientes-por-dni/${encodeURIComponent(dniNormalizado)}`,
          { credentials: 'include', signal: ac.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json?.found && json?.paciente) {
          setPacienteApi({
            dni: json.dni || dniNormalizado,
            paciente: json.paciente,
            obra_social: json.obra_social || '',
            source: json.source || 'geclisa',
            prestador: json.prestador_geclisa || null
          });
        } else {
          setPacienteApi(null);
        }
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.error('Error buscando paciente por DNI:', e);
          setPacienteApi(null);
        }
      } finally {
        if (!ac.signal.aborted) setPacienteLoading(false);
      }
    }, 320);

    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [open, dniNormalizado]);

  useEffect(() => {
    if (!open) return undefined;
    const ac = new AbortController();
    const timerId = setTimeout(async () => {
      setMedicamentosLoading(true);
      try {
        const q = String(medicamentoSearch || '').trim();
        const qs = new URLSearchParams();
        qs.set('limit', q ? '120' : '80');
        if (q) qs.set('q', q);
        const res = await fetch(
          `${API_BASE_URL}/api/asociacion-recetas/medicamentos?${qs.toString()}`,
          { credentials: 'include', signal: ac.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const meds = Array.isArray(json) ? json : (Array.isArray(json?.medicamentos) ? json.medicamentos : []);
        setMedicamentos(meds);
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.error('Error cargando vademécum alta rápida:', e);
        }
      } finally {
        if (!ac.signal.aborted) setMedicamentosLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timerId);
      ac.abort();
    };
  }, [open, medicamentoSearch]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const frame = requestAnimationFrame(() => {
      pickerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [pickerOpen]);

  const qMed = String(medicamentoSearch || '').trim();
  let medicamentosLista = [...medicamentos];
  if (
    medicamentoChosen
    && String(medicamentoId)
    && Number(medicamentoChosen.id) === Number(medicamentoId)
    && !medicamentosLista.some((m) => Number(m.id) === Number(medicamentoId))
  ) {
    medicamentosLista = [medicamentoChosen, ...medicamentosLista];
  }
  const selectedMedRow = medicamentosLista.find((m) => String(m.id) === String(medicamentoId))
    || medicamentoChosen;

  const handleSubmit = async () => {
    if (!dniNormalizado || dniNormalizado.length < 6) {
      onError?.('Ingresá un DNI válido (mínimo 6 dígitos)');
      return;
    }
    if (!Number.isFinite(medicamentoIdNum) || medicamentoIdNum <= 0) {
      onError?.('Seleccioná un medicamento');
      return;
    }

    const selectedMed = medicamentos.find((m) => Number(m.id) === medicamentoIdNum)
      || (medicamentoChosen && Number(medicamentoChosen.id) === medicamentoIdNum ? medicamentoChosen : null);
    const pacienteLabel = pacienteResuelto?.paciente
      ? `${pacienteResuelto.paciente} (DNI ${formatAltaRapidaDniDisplay(dniNormalizado)})`
      : `DNI ${formatAltaRapidaDniDisplay(dniNormalizado)}`;
    const medLabel = selectedMed ? formatMedicamentoOptionLabel(selectedMed) : 'medicamento seleccionado';
    const pamiLabel = esPami ? 'Paciente PAMI' : 'Sin marca PAMI';

    const confirmResult = await fireConfirmDialog({
      title: 'Crear pedido',
      html: buildCrmSwalBody({
        lead: `Pedido pendiente para <strong>${pacienteLabel}</strong>.`,
        items: [`Medicamento: <strong>${medLabel}</strong>`, pamiLabel, confirmQueueLabel]
      }),
      confirmButtonText: 'Crear pedido',
      cancelButtonText: 'Cancelar'
    });
    if (!confirmResult.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/alta-rapida`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dni: dniNormalizado,
          medicamento_id: medicamentoIdNum,
          es_pami: esPami,
          destino: destino === 'derivacion' ? 'derivacion' : 'aprobacion_medica'
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const nuevoRaw = await res.json();
      const medResolved = selectedMed
        || medicamentos.find((m) => Number(m.id) === medicamentoIdNum)
        || medicamentoChosen;
      const nuevo = mapAltaRapidaPedidoResponse(nuevoRaw, medicamentoIdNum, medResolved);
      if (pacienteResuelto?.paciente && (!nuevo.paciente || nuevo.paciente === nuevoRaw.paciente_dni)) {
        nuevo.paciente = pacienteResuelto.paciente;
      }
      if (pacienteResuelto?.obra_social && !nuevo.obra_social) {
        nuevo.obra_social = pacienteResuelto.obra_social;
      }
      resetForm();
      onPedidoCreated?.(nuevo);
    } catch (e) {
      console.error('Error creando pedido rápido:', e);
      onError?.(`Error creando pedido rápido: ${e.message || ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open && !submitting) {
    return (
      <div
        className="crm-alta-rapida-backdrop"
        style={{ opacity: 0, pointerEvents: 'none' }}
        aria-hidden
      />
    );
  }

  return (
    <div
      className="crm-alta-rapida-backdrop"
      style={{ opacity: open ? 1 : 0, pointerEvents: open ? 'all' : 'none' }}
    >
      <div className={`crm-alta-rapida-modal${isAltaStep ? ' crm-alta-rapida-modal--alta-step' : ''}`} role="dialog" aria-modal="true" aria-labelledby="crm-alta-rapida-title">
        <div className="crm-alta-rapida-modal__head">
          <div className="crm-alta-rapida-modal__eyebrow">{eyebrow}</div>
          <h2 id="crm-alta-rapida-title" className="crm-alta-rapida-modal__title">{title}</h2>
          <p className="crm-alta-rapida-modal__subtitle">{subtitle}</p>

          <WizardSteps steps={wizardSteps} currentStep={step} />
        </div>

        <div className={`crm-alta-rapida-modal__body${pickerOpen ? ' is-picker-open' : ''}`}>
          {isDniStep ? (
            <>
              <div className="crm-alta-rapida-modal__section">
                <label className="crm-alta-rapida-modal__label" htmlFor="crm-alta-rapida-dni">DNI del paciente</label>
                <input
                  id="crm-alta-rapida-dni"
                  className="crm-alta-rapida-modal__input"
                  list="crm-alta-rapida-dni-suggestions"
                  value={dni}
                  onChange={(e) => setDni(String(e.target.value || '').replace(/\D/g, ''))}
                  placeholder="Ej: 12345678"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={submitting}
                />
                <datalist id="crm-alta-rapida-dni-suggestions">
                  {(historialPacientes || []).map((p) => (
                    <option key={`dni-sug-${p.dni}`} value={p.dni}>
                      {`${p.paciente} · DNI ${formatAltaRapidaDniDisplay(p.dni)}`}
                    </option>
                  ))}
                </datalist>
              </div>

              <div className="crm-alta-rapida-modal__section">
                <PacienteDetectadoCard
                  dni={dni}
                  paciente={pacienteResuelto}
                  loading={pacienteLoading && dniNormalizado.length >= 6}
                />
              </div>

              {necesitaAltaPaciente ? (
                <div className="crm-alta-rapida-dni-next-hint">
                  <span className="crm-alta-rapida-dni-next-hint__icon" aria-hidden>→</span>
                  <span>Continuá al paso <strong>Alta</strong> para registrar la ficha del paciente.</span>
                </div>
              ) : null}
            </>
          ) : null}

          {isAltaStep ? (
            <AltaPacienteStep
              dni={dniNormalizado}
              disabled={submitting}
              onSuccess={handlePacienteExternoRegistrado}
              onError={onError}
              onRefetchDni={refetchPacientePorDni}
              onValidChange={setAltaFormValid}
              onSubmittingChange={setAltaSubmitting}
            />
          ) : null}

          {isMedStep ? (
            <div className="crm-alta-rapida-modal__section">
              <div className="crm-alta-rapida-step2-summary">
                <span className="crm-alta-rapida-step2-summary__label">Paciente</span>
                <strong>
                  {pacienteResuelto?.paciente || `DNI ${formatAltaRapidaDniDisplay(dniNormalizado)}`}
                </strong>
                {esPami ? <span className="crm-alta-rapida-step2-summary__badge">PAMI</span> : null}
              </div>

              <div className="crm-alta-rapida-modal__section crm-alta-rapida-modal__section--inline">
                <PamiToggle
                  value={esPami}
                  onChange={setEsPami}
                  disabled={submitting}
                />
              </div>

              <div className="crm-alta-rapida-med-box">
                <label className="crm-alta-rapida-modal__label" htmlFor="crm-alta-rapida-med-search">Medicamento</label>
                <input
                  id="crm-alta-rapida-med-search"
                  type="search"
                  className="crm-alta-rapida-modal__input"
                  value={medicamentoSearch}
                  onChange={(e) => setMedicamentoSearch(e.target.value)}
                  placeholder="Buscar por nombre, laboratorio o id…"
                  disabled={submitting}
                  aria-label="Buscar medicamento en vademécum"
                />
                {(medicamentosLoading || qMed || medicamentosLista.length > 0) ? (
                  <MedicamentoSearchStatus
                    loading={medicamentosLoading}
                    query={qMed}
                    count={medicamentosLista.length}
                  />
                ) : null}
                {!medicamentosLoading && qMed && medicamentosLista.length === 0 ? (
                  <p className="crm-alta-rapida-med-warn">No hay medicamentos que coincidan. Probá otro término.</p>
                ) : null}

                <div
                  ref={pickerRef}
                  className={`crm-alta-rapida-picker${pickerOpen ? ' is-open' : ''}${medicamentosLoading ? ' is-loading' : ''}`}
                >
                  <button
                    type="button"
                    className={`crm-alta-rapida-picker__trigger${!selectedMedRow || !Number(medicamentoId) ? ' is-empty' : ''
                      }${medicamentosLoading ? ' is-loading' : ''}`}
                    disabled={submitting}
                    onClick={() => {
                      if (submitting) return;
                      setPickerOpen((o) => !o);
                    }}
                    aria-expanded={pickerOpen}
                    aria-haspopup="listbox"
                    aria-busy={medicamentosLoading}
                  >
                    {medicamentosLoading ? (
                      <span className="crm-alta-rapida-picker__trigger-loading" aria-hidden>
                        <span className="crm-alta-rapida-picker__trigger-spinner" />
                      </span>
                    ) : null}
                    <span className="crm-alta-rapida-picker__trigger-label">
                      {medicamentosLoading
                        ? (qMed ? `Buscando “${qMed}”…` : 'Cargando vademécum…')
                        : (selectedMedRow && Number(medicamentoId) > 0
                          ? formatMedicamentoOptionLabel(selectedMedRow)
                          : 'Seleccionar medicamento del catálogo')}
                    </span>
                    <span className="crm-alta-rapida-picker__chevron" aria-hidden>
                      {medicamentosLoading ? '…' : '▾'}
                    </span>
                  </button>

                  {pickerOpen && !submitting ? (
                    <div className="crm-alta-rapida-picker__menu" role="listbox" aria-busy={medicamentosLoading}>
                      {medicamentosLoading ? (
                        <>
                          <div className="crm-alta-rapida-picker__menu-loading" role="status">
                            <span className="crm-alta-rapida-med-status__spinner" aria-hidden />
                            Actualizando resultados…
                          </div>
                          <MedicamentoPickerSkeleton rows={6} />
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            role="option"
                            className={`crm-alta-rapida-picker__option crm-alta-rapida-picker__option--placeholder${!medicamentoId ? ' is-selected' : ''}`}
                            aria-selected={!medicamentoId}
                            onClick={() => {
                              setMedicamentoId('');
                              setMedicamentoChosen(null);
                              setPickerOpen(false);
                            }}
                          >
                            — Sin selección —
                          </button>
                          {medicamentosLista.length === 0 ? (
                            <div className="crm-alta-rapida-picker__empty">
                              {qMed
                                ? 'No encontramos coincidencias. Probá con menos caracteres o el nombre genérico.'
                                : 'El catálogo está vacío o no respondió. Reintentá en unos segundos.'}
                            </div>
                          ) : null}
                          {medicamentosLista.map((m) => {
                            const selected = String(medicamentoId) === String(m.id);
                            return (
                              <button
                                key={`alta-med-${m.id}`}
                                type="button"
                                role="option"
                                className={`crm-alta-rapida-picker__option${selected ? ' is-selected' : ''}`}
                                aria-selected={selected}
                                onClick={() => {
                                  setMedicamentoId(String(m.id));
                                  setMedicamentoChosen(m);
                                  setPickerOpen(false);
                                }}
                              >
                                <span className="crm-alta-rapida-picker__option-label">
                                  {formatMedicamentoOptionLabel(m)}
                                </span>
                              </button>
                            );
                          })}
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="crm-alta-rapida-modal__footer">
          <button
            type="button"
            className="crm-alta-rapida-modal__btn crm-alta-rapida-modal__btn--ghost"
            onClick={onClose}
            disabled={submitting || altaSubmitting}
          >
            Cancelar
          </button>
          {!isDniStep ? (
            <button
              type="button"
              className="crm-alta-rapida-modal__btn crm-alta-rapida-modal__btn--ghost"
              onClick={goBack}
              disabled={submitting || altaSubmitting}
            >
              ← Atrás
            </button>
          ) : null}
          {isDniStep ? (
            <button
              type="button"
              className="crm-alta-rapida-modal__btn crm-alta-rapida-modal__btn--primary"
              onClick={goNextFromDni}
              disabled={submitting || !canLeaveDniStep}
            >
              Siguiente →
            </button>
          ) : null}
          {isAltaStep ? (
            <button
              type="submit"
              form="crm-alta-paciente-form"
              className="crm-alta-rapida-modal__btn crm-alta-rapida-modal__btn--primary crm-alta-rapida-modal__btn--alta"
              disabled={submitting || altaSubmitting || !altaFormValid}
            >
              {altaSubmitting ? 'Guardando…' : 'Registrar y continuar →'}
            </button>
          ) : null}
          {isMedStep ? (
            <button
              type="button"
              className="crm-alta-rapida-modal__btn crm-alta-rapida-modal__btn--primary"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              title={!canSubmit ? 'Seleccioná un medicamento' : undefined}
            >
              {submitting ? 'Creando…' : 'Crear pedido'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
